import { useEffect, useRef } from "react";
import { ArrowRight, Check, ChevronDown, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useCustomerEvidenceAccess } from "@/hooks/use-customer-evidence-access";
import { queryClient } from "@/lib/queryClient";
import type {
  EvidenceDistribution,
  EvidenceRefSelection,
  PersonaEvidencePostMatch,
  PersonaEvidenceLink,
} from "@shared/persona-evidence";
import { hasPersonaEvidenceMatch, safeMatchReason } from "@shared/persona-evidence";

type Props = {
  brandId: string | undefined;
  personaId: string;
  evidenceLink: PersonaEvidenceLink | null | undefined;
  profileAvailable?: boolean;
};

const dateLabel = (value: string | undefined) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Date unavailable"
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const matchLabels = {
  common_profile: "Matches a common buyer profile",
  adjacent: "Adjacent to a buyer profile",
  expansion: "Expansion segment",
} as const;

const unsafePanelWording = /\b(?:ideal|core|proven|validated)\b/i;
const panelCopy = (value: string | undefined, fallback: string) =>
  value && !unsafePanelWording.test(value) ? value : fallback;
const shareLabel = (pct: number) => Number(pct.toFixed(2)).toLocaleString();

function DistributionRow({
  label,
  distribution,
  selected,
  muted = false,
}: {
  label: string;
  distribution: EvidenceDistribution;
  selected: EvidenceRefSelection | { label: string; pct?: number; count?: number } | null;
  muted?: boolean;
}) {
  const values = distribution.values ?? [];
  const selectedValue = selected ? values.find((value) => value.label === selected.label) : undefined;
  const selectedLabel = selected ? panelCopy(selected.label, "Selected group") : null;
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-semibold tracking-wide text-foreground/80">{label}</h3>
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {distribution.denominator.toLocaleString()} observed
        </span>
      </div>
      <p className="text-[9px] leading-snug text-muted-foreground/75">
        {panelCopy(distribution.basis, "Aggregate share")} · Missing {distribution.missing == null ? "not reported" : distribution.missing.toLocaleString()}
      </p>
      <div
        className="flex h-2.5 overflow-hidden rounded-full bg-muted/80 ring-1 ring-inset ring-border/40"
        role="img"
        aria-label={`${label} distribution, denominator ${distribution.denominator}. ${values.map((value) => `${panelCopy(value.label, "Unlisted group")}: ${shareLabel(value.pct)}%`).join(", ")}`}
      >
        {values.map((value) => {
          const active = selectedValue?.id === value.id;
          return (
            <span
              key={value.id}
              className={`relative min-w-[2px] border-r border-card/80 ${active && !muted ? "bg-primary ring-1 ring-inset ring-foreground/60" : "bg-primary/25"}`}
              style={{ width: `${Math.max(0, Math.min(100, value.pct))}%` }}
              aria-hidden="true"
            />
          );
        })}
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {selected
          ? selectedValue
            ? <><span className="font-semibold text-foreground">{panelCopy(selectedValue.label, "Selected group")}</span> selected, {selectedValue.count.toLocaleString()} ({shareLabel(selectedValue.pct)}%)</>
            : <><span className="font-medium text-foreground/75">{selectedLabel}</span> selected, not listed in this distribution</>
          : "No close match"}
      </p>
      {selected && "importLabel" in selected && typeof selected.importLabel === "string" && (
        <p className="text-[10px] text-muted-foreground">Source: {panelCopy(selected.importLabel, "Evidence import")}</p>
      )}
    </div>
  );
}

const emptyDistribution: EvidenceDistribution = {
  denominator: 0,
  basis: "Distribution unavailable",
  missing: null,
  values: [],
};

function selectionForAttribute(
  generation: PersonaEvidenceLink["generation"],
  match: (PersonaEvidenceLink & PersonaEvidencePostMatch) | null,
  key: "bucket" | "seniority" | "companySize" | "industry",
) {
  if (generation && (generation.status === "valid" || generation.status === "partial") && generation.basis === "evidence") {
    return generation[key];
  }
  if (generation && (generation.status === "expansion" || generation.basis === "expansion")) {
    return null;
  }
  if (match) {
    return key === "bucket" ? match.bucket : match[key];
  }
  return null;
}

function generationHeadline(generation: NonNullable<PersonaEvidenceLink["generation"]>) {
  if (generation.status === "expansion" || generation.basis === "expansion") return "Built as an expansion segment";
  if ((generation.status === "valid" || generation.status === "partial") && generation.basis === "evidence") {
    if (generation.bucket) return `Generated from ${panelCopy(generation.bucket.label, "a reported buyer group")}`;
    const first = generation.seniority ?? generation.companySize ?? generation.industry;
    if (first) {
      if (generation.seniority && generation.companySize) {
        return `Generated from ${panelCopy(generation.seniority.label, "a reported seniority")} at ${panelCopy(generation.companySize.label, "a reported company size")} companies`;
      }
      return `Generated from ${panelCopy(first.label, "a reported buyer group")}`;
    }
  }
  return "No buyer group reported";
}

function choiceLabel(
  selected: EvidenceRefSelection | { label: string } | null | undefined,
  fallback: string,
) {
  return selected ? panelCopy(selected.label, "Reported buyer group") : fallback;
}

export function PersonaEvidencePanel({ brandId, personaId, evidenceLink, profileAvailable = true }: Props) {
  const access = useCustomerEvidenceAccess(brandId);
  const enabled = access.enabled;
  const matchAvailable = hasPersonaEvidenceMatch(evidenceLink);
  const pollStartedAt = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || matchAvailable || !profileAvailable) {
      pollStartedAt.current = null;
      return;
    }
    if (pollStartedAt.current === null) pollStartedAt.current = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - (pollStartedAt.current ?? Date.now()) >= 60_000) {
        window.clearInterval(timer);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
    }, 5_000);
    return () => {
      window.clearInterval(timer);
      pollStartedAt.current = null;
    };
  }, [enabled, matchAvailable, personaId, profileAvailable]);

  if (!enabled || !evidenceLink) return null;

  const link = evidenceLink;
  const hasMatch = hasPersonaEvidenceMatch(link);
  const generation = link.generation;
  const generationEvidence = !!generation &&
    (generation.status === "valid" || generation.status === "partial") &&
    generation.basis === "evidence";
  const generationExpansion = !!generation && (generation.status === "expansion" || generation.basis === "expansion");
  const isExpansion = generationExpansion || (!generationEvidence && hasMatch && link.matchType === "expansion");
  const selectedBucket = selectionForAttribute(generation, hasMatch ? link : null, "bucket");
  const selectedSeniority = selectionForAttribute(generation, hasMatch ? link : null, "seniority");
  const selectedCompanySize = selectionForAttribute(generation, hasMatch ? link : null, "companySize");
  const selectedIndustry = selectionForAttribute(generation, hasMatch ? link : null, "industry");
  const selectedForTitle = selectedBucket;
  const distributions = generationEvidence || generationExpansion
    ? generation!.distributions
    : hasMatch ? link.distributions : undefined;
  const visibleDistributions = {
    titles: distributions?.titles ?? emptyDistribution,
    seniority: distributions?.seniority ?? emptyDistribution,
    companySize: distributions?.companySize ?? emptyDistribution,
    industry: distributions?.industry ?? emptyDistribution,
  };
  const allTitles = [...(visibleDistributions.titles.values ?? [])].sort((a, b) => b.pct - a.pct || a.label.localeCompare(b.label));
  const topTitles = allTitles.slice(0, 6);
  const selectedTitleInTop = topTitles.some((title) => title.label === selectedForTitle?.label);
  const selectedTitleOutsideTop = !selectedTitleInTop && allTitles.find((title) => title.label === selectedForTitle?.label);
  const legacyReason = hasMatch ? safeMatchReason(link.reason, link.matchType) : "";
  const caveats = ((generationEvidence || generationExpansion ? generation!.caveats : hasMatch ? link.caveats : generation?.caveats) ?? [])
    .filter((caveat) => !unsafePanelWording.test(caveat));
  const bucketLabel = choiceLabel(selectedBucket, "No buyer group selected");
  const headline = generation && (generationEvidence || generationExpansion)
    ? generationHeadline(generation)
    : hasMatch ? "Matched against customer evidence" : "No buyer group reported";
  const exactGenerationExplanation = "Recorded from the generator. This shows which buyer groups it reports using. It does not confirm the persona's fears, motivations or preferences.";
  const reason = generation
    ? exactGenerationExplanation
    : legacyReason;
  const generationChoice = choiceLabel(
    generation?.bucket ?? generation?.seniority ?? generation?.companySize ?? generation?.industry,
    generationExpansion ? "Outside common buyer profiles" : "No buyer group reported",
  );
  const matchChoice = hasMatch
    ? choiceLabel(link.bucket, link.matchType === "expansion" ? "Outside common buyer profiles" : "No close match")
    : "No match recorded";
  const titleDenominator = visibleDistributions.titles.denominator;

  return (
    <section className="flex min-w-0 items-center">
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            className="inline-flex min-h-9 max-w-full flex-wrap items-center gap-2 rounded-lg border border-border/70 bg-card px-3 py-2 text-left text-xs font-semibold text-foreground/80 shadow-sm transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="button-persona-evidence-details"
          >
            <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
            <span>Where this persona came from</span>
            <span className="rounded-full border border-border/70 bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              Customer evidence
            </span>
          </button>
        </DialogTrigger>
        <DialogContent data-testid="persona-evidence-panel" className="flex max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] max-w-4xl flex-col gap-0 overflow-hidden border-border bg-card p-0 shadow-xl sm:max-h-[calc(100dvh-3rem)]">
          <DialogHeader data-testid="persona-evidence-header" className="shrink-0 border-b border-border/60 bg-primary/[0.035] px-4 py-4 pr-12 text-left sm:px-6 sm:pr-12">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <DialogTitle className="text-sm font-bold tracking-tight text-foreground">
                    Where this persona came from
                  </DialogTitle>
                  {hasMatch && (
                    <span className="rounded-full border border-border/70 bg-background/70 px-2 py-0.5 text-[10px] font-semibold text-foreground/75">
                      {matchLabels[link.matchType]}
                    </span>
                  )}
                  {link.agreement === "differ" && (
                    <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-200">Review</span>
                  )}
                </div>
                <DialogDescription className="mt-2 break-words text-xs leading-relaxed">
                  {generation ? (
                    <span className="font-medium text-foreground/85">{headline}</span>
                  ) : hasMatch ? (
                    <>Matched against <span className="font-medium text-foreground/80">{panelCopy(link.evidenceLabel, "customer evidence")}</span>, imported {dateLabel(link.evidenceImportedAt)}.</>
                  ) : (
                    <span className="font-medium text-foreground/85">{headline}</span>
                  )}
                </DialogDescription>
                {(generationEvidence || generationExpansion) && generation?.sources?.length ? (
                  <p className="mt-1 break-words text-[11px] leading-relaxed text-muted-foreground">
                    Evidence snapshot: {generation.sources.map((source) =>
                      `${panelCopy(source.importLabel, "Customer evidence import")}, imported ${dateLabel(source.importedAt)}`).join("; ")}. Directional sample.
                  </p>
                ) : null}
                {generation ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/80">{exactGenerationExplanation}</p>
                ) : hasMatch ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/80">
                    This import was one source used for matching. Persona generation may use other inputs.
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/80">
                    No match is available yet. No buyer group was reported for this persona.
                  </p>
                )}
                {generation && !hasMatch && !generationEvidence && !generationExpansion && (
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/80">
                    No match is available yet. No buyer group was reported for this persona.
                  </p>
                )}
                {link.agreement === "differ" && (
                  <p className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2.5 py-2 text-[11px] leading-relaxed text-foreground/80">
                    The generator and the match step chose different buyer groups
                    <span className="mt-1 block">Generator: {generationChoice}</span>
                    <span className="block">Match step: {matchChoice}</span>
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
                <span className="text-[10px] text-muted-foreground">
                  {generation ? `Recorded ${dateLabel(generation.recordedAt)}` : `Matched ${dateLabel(link.matchedAt)}`}
                </span>
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
            <div className="space-y-5">
              {generation?.droppedIds.length ? (
                <p className="rounded-md border border-border/50 bg-muted/25 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                  Some references from the generator were not recognized and were ignored.
                </p>
              ) : null}

              <details className="group rounded-lg border border-border/60 bg-muted/25">
                <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-3 text-xs font-semibold text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                  <span>Data limits</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true" />
                </summary>
                <div className="border-t border-border/50 px-3 py-3">
                  {generation && hasMatch ? (
                    <p className="mb-3 break-words text-[11px] leading-relaxed text-muted-foreground">
                      Match source: {panelCopy(link.evidenceLabel, "Customer evidence import")}. Imported {dateLabel(link.evidenceImportedAt)}. Matched {dateLabel(link.matchedAt)}.
                    </p>
                  ) : null}
                  {caveats.length ? (
                    <ul className="list-disc space-y-1 pl-4 text-[11px] leading-relaxed text-muted-foreground">
                      {caveats.map((caveat, index) => <li key={`${index}-${caveat}`}>{caveat}</li>)}
                    </ul>
                  ) : (
                    <p className="text-[11px] leading-relaxed text-muted-foreground">The available aggregate has limits. Use these shares as directional context, not a description of every buyer.</p>
                  )}
                </div>
              </details>

              {(hasMatch || generationEvidence || generationExpansion) ? (
                <div
                  className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1 sm:gap-2"
                  role="img"
                  aria-label={`Evidence flow: Closed-won deals, ${isExpansion ? "outside common buyer profiles" : `buyer group ${selectedBucket ? bucketLabel : "not selected"}`}, this persona`}
                  data-testid="persona-evidence-flow"
                >
                  <div className="flex min-h-20 min-w-0 flex-col items-start gap-1 rounded-lg border border-border/60 bg-background px-2 py-2 sm:min-h-12 sm:flex-row sm:items-center sm:gap-2 sm:px-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground">1</span>
                    <span className="text-xs font-medium text-foreground/80">Closed-won deals</span>
                  </div>
                  <ArrowRight className="h-3 w-3 text-muted-foreground/60 sm:h-4 sm:w-4" aria-hidden="true" />
                  <div className={`flex min-h-20 min-w-0 flex-col items-start gap-1 rounded-lg border px-2 py-2 sm:min-h-12 sm:flex-row sm:items-center sm:gap-2 sm:px-3 ${isExpansion ? "border-border/60 bg-muted/35" : "border-primary/35 bg-primary/[0.055]"}`}>
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${isExpansion ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}>2</span>
                    <span className="text-xs font-medium leading-snug text-foreground/80">
                      {isExpansion ? "Outside common buyer profiles" : `Buyer group: ${selectedBucket ? bucketLabel : "No close match"}`}
                    </span>
                  </div>
                  <ArrowRight className="h-3 w-3 text-muted-foreground/60 sm:h-4 sm:w-4" aria-hidden="true" />
                  <div className="flex min-h-20 min-w-0 flex-col items-start gap-1 rounded-lg border border-border/60 bg-background px-2 py-2 sm:min-h-12 sm:flex-row sm:items-center sm:gap-2 sm:px-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground">3</span>
                    <span className="text-xs font-medium text-foreground/80">This persona</span>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-3 text-xs text-muted-foreground">
                  No match has been recorded for this persona yet.
                </div>
              )}

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.12fr_0.88fr]">
                <div className="min-w-0 space-y-3">
                  <div className="flex flex-wrap items-end justify-between gap-2">
                    <div>
                      <h3 className="text-xs font-bold text-foreground">Closed-won job titles</h3>
                      <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                        {panelCopy(visibleDistributions.titles.basis, "Share of recorded titles")} · Missing {visibleDistributions.titles.missing == null ? "not reported" : visibleDistributions.titles.missing.toLocaleString()}
                      </p>
                    </div>
                    <span className="text-[10px] text-muted-foreground tabular-nums">n={titleDenominator.toLocaleString()}</span>
                  </div>
                  {topTitles.length ? (
                    <div className="space-y-2" role="list" aria-label="Top six observed job titles">
                      {topTitles.map((title) => {
                        const selected = title.label === selectedForTitle?.label;
                        return (
                          <div key={title.id} className="grid grid-cols-[minmax(0,1fr)_3.3rem] items-center gap-x-3 gap-y-1" role="listitem" aria-label={`${panelCopy(title.label, "Unlisted title")}: ${shareLabel(title.pct)} percent${selected ? ", selected for this persona" : ""}`}>
                            <div className="flex min-w-0 items-center gap-1.5">
                              {selected && !isExpansion ? <Check className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" /> : <span className="w-3 shrink-0" aria-hidden="true" />}
                              <span className={`truncate text-[11px] ${selected && !isExpansion ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{panelCopy(title.label, "Unlisted title")}</span>
                              {selected && !isExpansion && <span className="shrink-0 rounded border border-primary/40 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide text-primary">Selected</span>}
                            </div>
                            <span className="text-right text-[10px] tabular-nums text-muted-foreground">{shareLabel(title.pct)}%</span>
                            <div className="col-span-2 ml-4 h-1.5 overflow-hidden rounded-full bg-muted">
                              <div className={`h-full rounded-full ${!isExpansion && selected ? "bg-primary" : "bg-primary/25"}`} style={{ width: `${Math.max(0, Math.min(100, title.pct))}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {selectedForTitle && !selectedTitleInTop && (
                        <div className="mt-2 rounded-lg border border-primary/40 bg-primary/[0.04] px-3 py-2 text-[11px] leading-relaxed" data-testid="persona-evidence-title-outside-top-six">
                          <span className="font-semibold text-foreground">{panelCopy(selectedForTitle.label, "Selected buyer group")}</span>
                          {selectedTitleOutsideTop
                            ? <span className="text-muted-foreground"> is the selected title, below the six titles shown above: {shareLabel(selectedTitleOutsideTop.pct)}% ({selectedTitleOutsideTop.count.toLocaleString()} of {selectedTitleOutsideTop.denominator.toLocaleString()}).</span>
                            : <span className="text-muted-foreground"> is the selected title, below the six titles shown above. No title share is available.</span>}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="rounded-lg bg-muted/30 px-3 py-4 text-xs text-muted-foreground">
                      {hasMatch || generationEvidence || generationExpansion
                        ? "No title distribution was captured for this match."
                        : "No title distribution is available because no match has been recorded."}
                    </p>
                  )}
                  {isExpansion && <p className="rounded-md border border-border/60 bg-muted/35 px-3 py-2 text-[11px] font-medium text-muted-foreground">Not a common buyer profile in the closed-won data.</p>}
                  {selectedForTitle && "importLabel" in selectedForTitle && typeof selectedForTitle.importLabel === "string" && (
                    <p className="text-[10px] text-muted-foreground">Selected group source: {panelCopy(selectedForTitle.importLabel, "Evidence import")}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-1 lg:gap-3">
                  <DistributionRow label="Seniority" distribution={visibleDistributions.seniority} selected={selectedSeniority} muted={isExpansion} />
                  <DistributionRow label="Company size" distribution={visibleDistributions.companySize} selected={selectedCompanySize} muted={isExpansion} />
                  <DistributionRow label="Industry" distribution={visibleDistributions.industry} selected={selectedIndustry} muted={isExpansion} />
                </div>
              </div>

              <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-3">
                <p className="text-xs leading-relaxed text-foreground/80" data-testid="persona-evidence-reason">{reason}</p>
              </div>

              <div className="flex flex-col gap-3 border-t border-border/50 pt-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/35 bg-primary/[0.06] px-2.5 py-1 text-[10px] font-semibold text-foreground/80">
                    <span className="h-2 w-2 rounded-full bg-primary" /> Observed in the data
                  </span>
                  <span className="text-[10px] text-muted-foreground">Role context, seniority, company size, industry</span>
                </div>
              </div>

            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
